import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categories, categoryArticles, type CategoryArticle } from "@/mocks/categoryData";

const categoryMeta: Record<string, { title: string; desc: string; keywords: string }> = {
  platform: {
    title: "平台介绍 - 必赢国际官方平台",
    desc: "全方位了解必赢国际平台，包括平台介绍、操作指南、发展战略与核心价值。必赢国际是备受信赖的国际化综合平台。",
    keywords: "必赢国际平台介绍,必赢国际官方平台,必赢国际操作指南,必赢国际发展战略",
  },
  membership: {
    title: "会员权益 - 必赢国际会员体系",
    desc: "全面了解必赢国际会员等级体系与专属权益，从青铜到钻石，每级都有独特福利。加入必赢国际会员，享受尊贵服务体验。",
    keywords: "必赢国际会员,必赢国际会员权益,必赢国际VIP,必赢国际会员等级",
  },
  promotions: {
    title: "优惠活动 - 必赢国际限时福利",
    desc: "必赢国际最新优惠活动汇总，包括新会员首存豪礼、每日签到奖励、邀请好友共赢等限时福利，精彩不容错过。",
    keywords: "必赢国际优惠活动,必赢国际福利,必赢国际注册优惠,必赢国际活动",
  },
  security: {
    title: "安全保障 - 必赢国际安全体系",
    desc: "必赢国际安全保障体系全面解读，包括数据加密、资金托管、身份验证与风控系统，为用户提供安全可靠的服务环境。",
    keywords: "必赢国际安全,必赢国际保障,必赢国际数据安全,必赢国际风控",
  },
};

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug = "platform" } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  const filteredArticles = useMemo(() => {
    return categoryArticles.filter((a) => a.category === activeSlug);
  }, [activeSlug]);

  const meta = categoryMeta[activeSlug] || categoryMeta.platform;

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: meta.title,
    description: meta.desc,
    url: `https://www.biyingguoji.com/category/${activeSlug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "必赢国际",
      url: "https://www.biyingguoji.com",
    },
  };

  return (
    <div className="pt-16">
      <SEOHead
        title={meta.title}
        description={meta.desc}
        keywords={meta.keywords}
        canonicalUrl={`https://www.biyingguoji.com/category/${activeSlug}`}
        ogTitle={meta.title}
        ogDescription={meta.desc}
        structuredData={structuredData}
      />

      <div className="bg-background-100 border-b border-background-200/70">
        <Breadcrumb items={[{ label: t(activeCategory.nameKey) }]} />
        <div className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="text-center mb-10">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              {t(activeCategory.nameKey)}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              {activeSlug === "platform" && "探索必赢国际平台的方方面面，从入门到精通"}
              {activeSlug === "membership" && "了解会员体系，解锁属于您的专属权益"}
              {activeSlug === "promotions" && "最新活动一手掌握，福利优惠不错过"}
              {activeSlug === "security" && "全方位安全保障，让您安心无忧"}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-200 hover:text-primary-600"
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {t(cat.nameKey)}
              </button>
            ))}
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {filteredArticles.map((article: CategoryArticle) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group block p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
              >
                <div className="flex items-center gap-2 text-xs text-foreground-500 mb-3">
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                  {article.summary}
                </p>
                <div className="mt-4 flex items-center gap-1 text-sm text-primary-600 font-medium">
                  <span>{t("article.read_more")}</span>
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}